<div class="flex flex-col bg-white rounded-xl shadow-2xl w-[780px] max-h-[85vh]">

  <!-- Header -->
  <div class="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
    <div>
      <div class="flex items-center gap-2">
        <h2 class="text-xl font-semibold text-momentum-deep_purple">Changelog</h2>
        <app-info-modal-button [data]="changelogInfo"></app-info-modal-button>
      </div>
      <p class="text-sm text-agentium_gray-text mt-0.5">{{ data.marketplaceName }}</p>
    </div>
    <button class="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded" (click)="close()">
      <fa-icon [icon]="faXmark" size="lg"></fa-icon>
    </button>
  </div>

  <!-- Body -->
  <div class="flex-1 overflow-y-auto px-6 py-4 relative min-h-[300px]">

    <!-- Loading -->
    @if (isLoading) {
      <div class="absolute inset-0 flex items-center justify-center">
        <fa-icon [icon]="faGear" animation="spin" class="text-momentum-deep_purple" size="2xl"></fa-icon>
      </div>
    }

    <!-- Error -->
    @if (!isLoading && hasError) {
      <div class="flex flex-col items-center justify-center h-48 text-agentium_gray-text">
        <p class="text-base font-medium text-red-500">Failed to load changelog.</p>
        <p class="text-sm mt-1">Please close and try again.</p>
      </div>
    }

    <!-- Empty -->
    @if (!isLoading && !hasError && entries.length === 0) {
      <div class="flex flex-col items-center justify-center h-48 text-agentium_gray-text">
        <p class="text-base font-medium">No changes recorded yet.</p>
      </div>
    }

    <!-- Timeline -->
    @if (!isLoading && !hasError && entries.length > 0) {
      <div class="relative">
        <!-- Vertical line -->
        <div class="absolute left-[7px] top-2 bottom-2 w-0.5 bg-gray-200"></div>

        <ul class="space-y-5">
          @for (entry of entries; track entry.id) {
            <li class="flex gap-4 pl-6 relative">
              <!-- Dot -->
              <span
                class="absolute left-0 top-1.5 w-3.5 h-3.5 rounded-full border-2 border-white ring-2 flex-shrink-0"
                [ngClass]="getDotClass(entry.changeCategory)"
              ></span>

              <div class="flex-1 bg-gray-50 border border-gray-100 rounded-lg px-4 py-3 hover:bg-white hover:border-gray-200 transition-colors">
                <div class="flex flex-wrap items-center justify-between gap-2 mb-2">
                  <div class="flex items-center gap-2 flex-wrap">
                    <span
                      class="text-xs font-semibold px-2 py-0.5 rounded-full uppercase tracking-wide"
                      [ngClass]="getCategoryClass(entry.changeCategory)"
                    >{{ entry.changeCategory }}</span>
                    <span class="text-sm font-medium text-momentum-deep_purple">{{ entry.fieldName }}</span>
                  </div>
                  <span class="text-xs text-agentium_gray-text whitespace-nowrap">{{ formatDate(entry.changedAt) }}</span>
                </div>

                <!-- Value change -->
                @if (entry.changeCategory !== 'CREATED') {
                  @if (isCollectionField(entry.fieldName)) {
                    <!-- Collection field: chips per item with Removed / Added labels -->
                    <div class="flex flex-col gap-1.5 text-sm">
                      @if (entry.oldValue) {
                        <div class="flex items-center gap-1.5 flex-wrap">
                          <span class="text-xs text-agentium_gray-text font-medium w-16 flex-shrink-0">Removed:</span>
                          @for (item of parseCollectionValue(entry.oldValue); track item) {
                            <span class="bg-red-50 text-red-600 px-2 py-0.5 rounded text-xs font-medium">{{ item }}</span>
                          }
                        </div>
                      }
                      @if (entry.newValue) {
                        <div class="flex items-center gap-1.5 flex-wrap">
                          <span class="text-xs text-agentium_gray-text font-medium w-16 flex-shrink-0">Added:</span>
                          @for (item of parseCollectionValue(entry.newValue); track item) {
                            <span class="bg-green-50 text-green-700 px-2 py-0.5 rounded text-xs font-medium">{{ item }}</span>
                          }
                        </div>
                      }
                    </div>
                  } @else {
                    <!-- Scalar field: old → new -->
                    <div class="flex items-center gap-2 text-sm flex-wrap">
                      <span class="bg-red-50 text-red-600 px-2 py-0.5 rounded line-through break-all">
                        {{ entry.oldValue ?? '—' }}
                      </span>
                      <span class="text-agentium_gray-text">→</span>
                      <span class="bg-green-50 text-green-700 px-2 py-0.5 rounded font-medium break-all">
                        {{ entry.newValue ?? '—' }}
                      </span>
                    </div>
                  }
                } @else {
                  <div class="text-sm text-agentium_gray-text">Marketplace created.</div>
                }

                <!-- Changed by -->
                <div class="mt-2 text-xs text-agentium_gray-text">
                  by <span class="font-medium text-gray-700">{{ entry.changedBy }}</span>
                </div>
              </div>
            </li>
          }
        </ul>
      </div>
    }
  </div>

  <!-- Footer / Pagination -->
  @if (!isLoading && !hasError && totalElements > 0) {
    <div class="flex items-center justify-between px-6 py-3 border-t border-gray-200 flex-shrink-0">
      <span class="text-sm text-agentium_gray-text">
        Showing {{ (currentPage - 1) * pageSize + 1 }}–{{ displayEnd }} of {{ totalElements }}
      </span>
      <div class="flex items-center gap-2">
        <button
          class="px-3 py-1.5 text-sm bg-momentum-deep_purple text-white rounded disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
          [disabled]="currentPage <= 1"
          (click)="previousPage()"
        >
          <fa-icon [icon]="faChevronLeft" size="xs"></fa-icon>
          Previous
        </button>
        <span class="text-sm text-agentium_gray-text">Page {{ currentPage }} of {{ totalPages }}</span>
        <button
          class="px-3 py-1.5 text-sm bg-momentum-deep_purple text-white rounded disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
          [disabled]="currentPage >= totalPages"
          (click)="nextPage()"
        >
          Next
          <fa-icon [icon]="faChevronRight" size="xs"></fa-icon>
        </button>
      </div>
    </div>
  }

</div>
